// Angular
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
// RxJS
import { Observable } from 'rxjs';
// CRUD
import { HttpUtilsService, QueryParamsModel, QueryResultsModel } from '../../_base/crud';
// Models
import { PatientWeightModel } from '../_models/patient-weight.model';

// Ngrx
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { currentAuthToken, currentUser } from '../../auth/_selectors/auth.selectors';
import { environment } from '../../../../environments/environment';

@Injectable()
export class PatientWeightsService {
  private authToken;
  private companyId;

  constructor(
    private http: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
    this.store.pipe(select(currentUser)).subscribe((res) => (this.companyId = res.companyID));
  }

  // CREATE =>  POST: add a new patientWeight to the server
  createPatientWeight(
    patientWeight: PatientWeightModel,
    id: number,
  ): Observable<PatientWeightModel> {
    // Note: Add headers if needed (tokens/bearer)
    const url = environment.CORE_BASE + `/companies/${this.companyId}/patients/${id}/weights`;
    const body = patientWeight;
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.post<PatientWeightModel>(url, body, { headers: httpHeaders });
  }

  // READ
  getAllPatientWeights(): Observable<PatientWeightModel[]> {
    const url = environment.CORE_BASE + '/all-patient';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<PatientWeightModel[]>(url, { headers: httpHeaders });
  }

  getPatientWeightById(patientWeightId: number): Observable<PatientWeightModel> {
    const body = {
      id: patientWeightId,
    };
    const url = `environment.CORE_BASE/companies/${this.companyId}/patients?page=0&size=10`;
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.post<PatientWeightModel>(url, body, { headers: httpHeaders });
  }

  // Method from server should return QueryResultsModel(items: any[], totalsCount: number)
  // items => filtered/sorted result
  // Server should return filtered/sorted result
  findPatientWeights(queryParams: QueryParamsModel, id: number): Observable<QueryResultsModel> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = environment.CORE_BASE + `/companies/${this.companyId}/patients/${id}/weights`;
    return this.http.get<QueryResultsModel>(url, { headers: httpHeaders });
  }

  // READ
  getPatientWeights(id): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = environment.CORE_BASE + `/companies/${this.companyId}/patients/${id}/weights`;
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  // UPDATE => PUT: update the patient on the server
  updatePatientWeight(patientWeight: PatientWeightModel): Observable<any> {
    const httpHeader = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = environment.CORE_BASE + `/companies/${this.companyId}/patients/${patientWeight.id}`;
    const body = patientWeight;
    return this.http.put(url, body, { headers: httpHeader });
  }

  // UPDATE Status
  updateStatusForPatientWeight(id: number, isActive: boolean): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      id,
      isActive,
    };
    const url = environment.CORE_BASE + '/patientstatusupdatesingle';
    return this.http.put(url, body, { headers: httpHeaders });
  }

  // UPDATE Status
  updateStatusForPatientWeights(ids: number[], isActive: boolean): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      ids,
      isActive,
    };
    const url = environment.CORE_BASE + '/patientstatusupdate';
    return this.http.put(url, body, { headers: httpHeaders });
  }

  // DELETE => delete the patient from the server
  deletePatientWeight(patientWeightId: number): Observable<any> {
    const url = environment.CORE_BASE + `/companies/${this.companyId}/patients/${patientWeightId}`;
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.delete<any>(url, { headers: httpHeaders });
  }

  deletePatientWeights(patientWeightsIds: number[] = []): Observable<any> {
    const url = environment.CORE_BASE + '/multiple-soft-delete-patient';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      ids: patientWeightsIds,
    };
    return this.http.post<any>(url, body, { headers: httpHeaders });
  }
}
